import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControl, TextField } from '@mui/material';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useQueryClient, type UseMutationResult } from 'react-query';
import type { ZodSchema } from 'zod';

export type FormField<FormType extends FieldValues> = {
  name: FieldPath<FormType>;
  label: string;
};

export type ConnectOAuthModalProps<FormType extends FieldValues> = {
  open: boolean;
  onClose: () => void;
  fields: FormField<FormType>[];
  schema: ZodSchema<FormType>;
  mutationHook: () => UseMutationResult<unknown, unknown, FormType>;
  dialogTitle: string;
};

export function ConnectOAuthModal<FormType extends FieldValues>({ open, onClose, fields, schema, mutationHook, dialogTitle }: ConnectOAuthModalProps<FormType>): React.ReactElement {
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
  });

  const queryClient = useQueryClient();

  const handleSuccess = async () => {
    onClose();
    await queryClient.invalidateQueries('connectedIntegrations');
  };

  const mutation = mutationHook();

  const handleSubmit = form.handleSubmit(async (formData) => {
    await runMutation(
      mutation,
      formData,
      {
        pending: 'Connecting...',
        success: 'Connected',
        error: 'Failed to connect',
      },
      {
        onSuccess: handleSuccess,
      },
    );
  });

  return (
    <VyDialog
      component="form"
      open={open}
      onClose={onClose}
      label={dialogTitle}
      ctaText="Submit"
      cancelButtonText="Cancel"
      onSubmit={handleSubmit}
      warningText={mutation.isError ? 'Failed connecting' : undefined}
      warningColor="#D32F2F"
    >
      <FormControl sx={{ padding: '10px', width: '100%', marginBottom: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fields.map((field) => (
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              variant="filled"
              size="medium"
              {...form.register(field.name)}
            />
          ))}
        </Box>
      </FormControl>
    </VyDialog>
  );
}
